import isEqual from 'lodash-es/isEqual'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Toasts } from '@fourthwall/components'
import type { DomainDnsRecords } from '@fourthwall/services/dist/DnsApiClient/models'
import type {
  EntriEventDetail,
  PartialEntriConfig,
} from '@modules/Dashboard/pages/Settings/pages/Domain/providers/Entri'
import { useEntriContext } from '@modules/Dashboard/pages/Settings/pages/Domain/providers/Entri'
import { routing } from '@utils/routing'

import { useCurrentShopQuery } from '../../../../../hooks/useCurrentShopQuery'
import { useDnsQuery } from '../../../../../hooks/useDnsQuery'
import { useDetectDomainProviderGetMutation } from '../queries/useDetectDomainProviderGetMutation'
import { useUpdateEntriProviderDetailsMutation } from '../queries/useUpdateEntriProviderDetails'
import { useVerifyDomainMutation } from '../queries/useVerifyDomainMutation'
import { getEntriConfig } from '../utils/getEntriConfig'
import { getUpdateEntriProviderDetailsBody } from '../utils/getUpdateEntriProviderDetailsBody'

export type useEntriProps = {
  enabled?: boolean
}

export const useEntri = (options: useEntriProps = { enabled: true }) => {
  const navigate = useNavigate()
  const { currentShopQuery } = useCurrentShopQuery()
  const { updateEntriProviderDetailsMutation } = useUpdateEntriProviderDetailsMutation()
  const { verifyDomainMutation } = useVerifyDomainMutation()
  const { invalidateDnsQuery } = useDnsQuery()
  const { detectDomainProviderGetMutation } = useDetectDomainProviderGetMutation()
  const [isAutomaticSetupInProgress, setIsAutomaticSetupInProgress] = useState(false)
  const shopId = currentShopQuery.data?.id
  const { isEntriLoaded, enableEntri, addEntriCloseEvent, removeEntriCloseEvent, showEntriModal } =
    useEntriContext()

  useEffect(() => {
    if (options.enabled) {
      enableEntri()
    }
  }, [enableEntri, options.enabled])

  const getProviderDetails = (
    dnsList: DomainDnsRecords,
    options?: Parameters<typeof detectDomainProviderGetMutation.mutate>[1],
  ) => {
    if (!shopId) return

    detectDomainProviderGetMutation.mutate(
      { dnsList, shopId },
      {
        ...options,
        onSuccess: async (data) => {
          const providerDetails =
            data.domainType && getUpdateEntriProviderDetailsBody(data.domainType)

          if (providerDetails && !isEqual(providerDetails, dnsList.entriProviderDetails)) {
            await updateEntriProviderDetailsMutation.mutateAsync([
              { domainId: dnsList.domainId },
              providerDetails,
            ])
          }

          options?.onSuccess?.(data, { dnsList, shopId }, undefined)
        },
      },
    )
  }

  const verifyDomain = async (
    event: Event & EntriEventDetail,
    options?: Parameters<typeof verifyDomainMutation.mutate>[1],
  ) => {
    if (event.detail.success) {
      await verifyDomainMutation.mutateAsync([], {
        ...options,
      })
    } else {
      navigate(routing.settings.domain.configuration)
    }

    removeEntriCloseEvent(verifyDomain)
    setIsAutomaticSetupInProgress(false)
  }

  const setUpDomainAutomatically = (
    config: PartialEntriConfig,
    options?: Parameters<typeof verifyDomain>[1],
  ) => {
    setIsAutomaticSetupInProgress(true)
    showEntriModal(config)
    addEntriCloseEvent((event) => verifyDomain(event, options))
  }

  const navigateToManualConfiguration = () => navigate(routing.settings.domain.configuration)

  const connectDomain = (dnsList: DomainDnsRecords, onConnect?: () => void) => {
    getProviderDetails(dnsList, {
      onSuccess: ({ config, domainType }) => {
        if (!domainType || domainType?.setupType === 'Manual') {
          navigateToManualConfiguration()
        }

        if (domainType!.setupType === 'Automatic') {
          setUpDomainAutomatically(config, {
            onSuccess: ({ dnsList }) => {
              invalidateDnsQuery()

              const toastMessage = dnsList.areAllEntriesComplete
                ? `${dnsList.domain} was added and is currently syncing.`
                : `${dnsList.domain} was added but requires additional verification.`

              Toasts.notify(toastMessage, {
                type: 'info',
              })
            },
            onError: navigateToManualConfiguration,
            onSettled: () => {
              navigate(routing.settings.domain.self)
            },
          })
        }
      },
      onError: navigateToManualConfiguration,
      onSettled: onConnect,
    })
  }

  return {
    isEntriLoaded,
    isAutomaticSetupInProgress,
    isGettingProviderDetailsInProgress: detectDomainProviderGetMutation.isLoading,
    getEntriConfig,
    getProviderDetails,
    connectDomain,
    setUpDomainAutomatically,
  }
}
